import { useQueries } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  AlertTriangle,
  Banknote,
  Building2,
  FileMinus2,
  FileText,
  LayoutDashboard,
  MapPin,
  Percent,
  Receipt,
  ShieldAlert,
  Wallet,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Seccion } from '@/shared/ui/Seccion';
import { Barras, Evolucion, Reparto, enDinero, enPorcentaje } from '@/shared/ui/graficos';
import type { Emisor } from '@/shared/api/tipos';
import { Identidad } from './identidad';
import { Alertas, Cifra, type Alerta } from './piezas';

interface Comparativo {
  total: number;
  totalAntes: number;
  variacion: number | null;
}

interface Ventas {
  facturas: number;
  facturado: number;
  notas: number;
  acreditado: number;
  neto: number;
  notasSinAutorizar: number;
}

interface Tramo {
  antiguedad: string;
  clientes: number;
  facturas: number;
  monto: number;
}

interface Cartera {
  total: number;
  vencido: number;
  facturas: number;
  tramos: Tramo[];
}

interface Sri {
  emitidas: number;
  autorizadas: number;
  rechazadas: number;
  enCola: number;
}

interface Parroquia {
  parroquia: string;
  cuentas: number;
}

interface Catastro {
  clientes: number;
  cuentas: number;
  exonerados: number;
  mensual: number;
  parroquias: Parroquia[];
}

interface Dia {
  dia: string;
  cobros: number;
  total: number;
}

interface Factura {
  id: number;
  cliente: string;
  identificacion: string;
  periodo: string;
  vencimiento: string;
  total: number;
  estado: string;
}

interface Hoja {
  contenido: Factura[];
}

function enIso(fecha: Date): string {
  const dosCifras = (n: number) => String(n).padStart(2, '0');
  return `${fecha.getFullYear()}-${dosCifras(fecha.getMonth() + 1)}-${dosCifras(fecha.getDate())}`;
}

function enFecha(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return dia && mes && anio ? `${dia}/${mes}/${anio}` : iso;
}

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const ESTADOS: Record<string, { rotulo: string; clase: string }> = {
  pagada: { rotulo: 'Pagada', clase: 'bg-marca-50 text-marca-700' },
  pendiente: { rotulo: 'Pendiente', clase: 'bg-amber-50 text-amber-700' },
  vencida: { rotulo: 'Vencida', clase: 'bg-red-50 text-red-700' },
  anulada: { rotulo: 'Anulada', clase: 'bg-armazon-100 text-armazon-500' },
  acreditada: { rotulo: 'Acreditada', clase: 'bg-blue-50 text-blue-700' },
};

const ICONO_DE_ALERTA: Record<string, LucideIcon> = {
  firma: ShieldAlert,
  rechazadas: XCircle,
  enCola: FileText,
  notas: FileMinus2,
};

export function PaginaInicio() {
  const hoy = new Date();
  const hasta = enIso(hoy);
  const desde = enIso(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
  const haceUnaSemana = enIso(new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - 6));

  const mesEnLetra = `${MESES[hoy.getMonth()]} de ${hoy.getFullYear()}`;

  const [emisor, recaudacion, ventas, cartera, sri, catastro, diaria, facturas] = useQueries({
    queries: [
      { queryKey: ['emisor'], queryFn: () => api.get<Emisor>('/emisor') },
      {
        queryKey: ['inicio', 'recaudacion', desde, hasta],
        queryFn: () =>
          api.get<Comparativo>('/informes/recaudacion/comparativo', { query: { desde, hasta } }),
      },
      {
        queryKey: ['inicio', 'ventas', desde, hasta],
        queryFn: () =>
          api.get<Ventas>('/informes/facturacion/ventas/panel', { query: { desde, hasta } }),
      },
      {
        queryKey: ['inicio', 'cartera'],
        queryFn: () => api.get<Cartera>('/informes/facturacion/cartera/panel'),
      },
      {
        queryKey: ['inicio', 'sri', desde, hasta],
        queryFn: () => api.get<Sri>('/informes/facturacion/sri/panel', { query: { desde, hasta } }),
      },
      {
        queryKey: ['inicio', 'catastro'],
        queryFn: () => api.get<Catastro>('/informes/catastro/panel'),
      },
      {
        queryKey: ['inicio', 'diaria', haceUnaSemana, hasta],
        queryFn: () =>
          api.get<Dia[]>('/informes/recaudacion/diaria', {
            query: { desde: haceUnaSemana, hasta },
          }),
      },
      {
        queryKey: ['inicio', 'facturas'],
        queryFn: () => api.get<Hoja>('/facturas', { query: { tamanio: '6' } }),
      },
    ],
  });

  if (emisor.isLoading) {
    return <Cargando titulo="Cargando" detalle="reuniendo las cifras del mes" />;
  }

  const morosidad =
    cartera.data && cartera.data.total > 0
      ? (cartera.data.vencido / cartera.data.total) * 100
      : null;

  const alertas: Alerta[] = [];
  const certificado = emisor.data?.certificado;

  if (emisor.data && (!certificado || !certificado.vigente)) {
    alertas.push({
      clave: 'firma',
      titulo: certificado ? 'La firma electrónica caducó' : 'No hay firma electrónica cargada',
      detalle: 'Sin ella no se puede emitir ningún comprobante',
      gravedad: 'urgente',
      a: '/configuracion',
    });
  } else if (certificado?.porCaducar) {
    alertas.push({
      clave: 'firma',
      titulo: `La firma caduca en ${certificado.diasRestantes.toLocaleString('es-EC')} días`,
      detalle: `Vence el ${enFecha(certificado.validoHasta)}: renuévela antes de la emisión`,
      gravedad: 'atencion',
      a: '/configuracion',
    });
  }

  if (sri.data && sri.data.rechazadas > 0) {
    alertas.push({
      clave: 'rechazadas',
      titulo: `${sri.data.rechazadas.toLocaleString('es-EC')} comprobantes rechazados por el SRI`,
      detalle: 'No están declarados: hay que corregirlos y volver a enviarlos',
      gravedad: 'urgente',
      a: '/proceso-sri',
    });
  }

  if (sri.data && sri.data.enCola > 0) {
    alertas.push({
      clave: 'enCola',
      titulo: `${sri.data.enCola.toLocaleString('es-EC')} comprobantes en cola`,
      detalle: 'Emitidos y todavía sin autorizar por el SRI',
      gravedad: 'atencion',
      a: '/proceso-sri',
    });
  }

  if (ventas.data && ventas.data.notasSinAutorizar > 0) {
    alertas.push({
      clave: 'notas',
      titulo: `${ventas.data.notasSinAutorizar.toLocaleString('es-EC')} notas de crédito sin autorizar`,
      detalle: 'La corrección está pedida pero no resta todavía de la declaración',
      gravedad: 'atencion',
      a: '/notas-credito',
    });
  }

  const porParroquia = [...(catastro.data?.parroquias ?? [])].sort((a, b) => b.cuentas - a.cuentas);
  const reparto = [
    ...porParroquia.slice(0, 5).map((p) => ({ nombre: p.parroquia, valor: p.cuentas })),
    ...(porParroquia.length > 5
      ? [
          {
            nombre: 'Las demás',
            valor: porParroquia.slice(5).reduce((suma, p) => suma + p.cuentas, 0),
          },
        ]
      : []),
  ];

  const porDia = (diaria.data ?? []).map((d) => ({ ...d, etiqueta: enFecha(d.dia).slice(0, 5) }));

  return (
    <>
      <EncabezadoDePagina
        icono={LayoutDashboard}
        titulo="Dashboard"
        subtitulo={`Resumen general — ${mesEnLetra}`}
        acciones={
          <>
            <Enlace a="/emision" icono={Receipt} rotulo="Emisión" />
            <Enlace a="/cartera" icono={Wallet} rotulo="Cartera" />
          </>
        }
      />

      <div className="space-y-4">
        {emisor.data && <Identidad emisor={emisor.data} />}

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
          <Cifra
            icono={FileText}
            rotulo="Facturado del mes"
            valor={enDinero(ventas.data?.facturado ?? 0)}
            pie={`${(ventas.data?.facturas ?? 0).toLocaleString('es-EC')} comprobantes`}
            tono="azul"
            a="/facturas"
          />
          <Cifra
            icono={Banknote}
            rotulo="Recaudado del mes"
            valor={enDinero(recaudacion.data?.total ?? 0)}
            {...(recaudacion.data?.variacion === undefined
              ? {}
              : { variacion: recaudacion.data.variacion })}
            pie="vs mes anterior"
            tono="esmeralda"
            a="/pagos"
          />
          <Cifra
            icono={AlertTriangle}
            rotulo="Cartera vencida"
            valor={enDinero(cartera.data?.vencido ?? 0)}
            pie={`${(cartera.data?.facturas ?? 0).toLocaleString('es-EC')} facturas`}
            tono="rojo"
            a="/cartera"
          />
          <Cifra
            icono={Percent}
            rotulo="Morosidad"
            valor={morosidad === null ? '—' : enPorcentaje(morosidad)}
            pie="de la cartera viva"
            tono="ambar"
            a="/reportes"
          />
          <Cifra
            icono={MapPin}
            rotulo="Predios en el padrón"
            valor={(catastro.data?.cuentas ?? 0).toLocaleString('es-EC')}
            pie={`${(catastro.data?.exonerados ?? 0).toLocaleString('es-EC')} exonerados`}
            tono="violeta"
            a="/clientes"
          />
          <Cifra
            icono={Building2}
            rotulo="Autorizados por el SRI"
            valor={`${(sri.data?.autorizadas ?? 0).toLocaleString('es-EC')} / ${(
              sri.data?.emitidas ?? 0
            ).toLocaleString('es-EC')}`}
            pie={
              sri.data && sri.data.emitidas > 0
                ? `${enPorcentaje((sri.data.autorizadas / sri.data.emitidas) * 100)} del mes`
                : 'sin emisión este mes'
            }
            tono="pizarra"
            a="/proceso-sri"
          />
        </div>

        <div className="grid gap-4 xl:grid-cols-3">
          <Seccion
            titulo="Recaudación diaria"
            ayuda="Lo que entró por ventanilla en los últimos siete días"
            ancho="xl:col-span-2"
          >
            <Evolucion datos={porDia} claveX="etiqueta" claveY="total" nombre="Recaudado" />
          </Seccion>

          <Seccion titulo="El padrón, por parroquia" ayuda="Predios activos en cada una">
            <Reparto datos={reparto} />
          </Seccion>
        </div>

        <div className="grid gap-4 xl:grid-cols-3">
          <Seccion
            titulo="La deuda, por antigüedad"
            ayuda="Cuánto lleva vencida la cartera de hoy"
            ancho="xl:col-span-2"
          >
            <Barras
              datos={cartera.data?.tramos ?? []}
              claveEtiqueta="antiguedad"
              claveValor="monto"
              nombre="Deuda"
            />
          </Seccion>

          <Seccion titulo="Lo que hay que mirar" ayuda="Sólo aparece lo que está pasando">
            <Alertas alertas={alertas} icono={ICONO_DE_ALERTA} />
          </Seccion>
        </div>

        <Seccion titulo="Facturación reciente" ayuda="Las últimas facturas emitidas">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-armazon-50/80">
                <tr>
                  {['Cliente', 'Período', 'Valor', 'Estado', 'Vence'].map((c, i) => (
                    <th
                      key={c}
                      scope="col"
                      className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-armazon-500 ${
                        i === 2 ? 'text-right' : i === 0 || i === 1 ? 'text-left' : 'text-center'
                      }`}
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-armazon-100">
                {(facturas.data?.contenido ?? []).map((f) => {
                  const estado = ESTADOS[f.estado] ?? {
                    rotulo: f.estado,
                    clase: 'bg-armazon-100 text-armazon-600',
                  };

                  return (
                    <tr key={f.id} className="transition-colors hover:bg-armazon-50/60">
                      <td className="px-3 py-2.5 text-xs text-armazon-700">
                        {f.cliente}
                        <span className="block text-[11px] tabular-nums text-armazon-400">
                          {f.identificacion}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-xs tabular-nums text-armazon-500">
                        {f.periodo}
                      </td>
                      <td className="px-3 py-2.5 text-right text-xs font-semibold tabular-nums">
                        {enDinero(f.total)}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${estado.clase}`}
                        >
                          {estado.rotulo}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-center text-xs tabular-nums text-armazon-500">
                        {enFecha(f.vencimiento)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="px-3 pt-2 text-right">
            <Link
              to="/facturas"
              className="text-xs font-semibold text-marca-700 underline-offset-2 hover:underline"
            >
              Ver todas las facturas
            </Link>
          </div>
        </Seccion>
      </div>
    </>
  );
}

function Enlace({ a, icono: Icono, rotulo }: { a: string; icono: LucideIcon; rotulo: string }) {
  return (
    <Link
      to={a}
      className="inline-flex h-9 items-center gap-2 rounded-xl border border-armazon-200 bg-white px-3 text-xs font-semibold text-armazon-700 transition hover:border-armazon-300 hover:bg-armazon-50"
    >
      <Icono className="size-4 text-armazon-400" aria-hidden />
      {rotulo}
    </Link>
  );
}
